import React from "react";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { RadioGroup } from "@progress/kendo-react-inputs";
import { CgDanger } from "react-icons/cg";
import IconComponent from "../common/IconComponent";

interface BaseOption {
  value: string;
  label: string;
  [key: string]: any;
}

interface CardConfig {
  layout: "country" | "visa" | "processing";
  showBadge?: boolean;
  badgePosition?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  showPrice?: boolean;
  pricePrefix?: string;
  showSubtitle?: boolean;
  showDescription?: boolean;
  showCode?: boolean;
  codePosition?: "left" | "right" | "top";
  containerClass?: string;
  cardClass?: string;
}

interface CardRadioGroupProps extends FieldRenderProps {
  options: BaseOption[];
  cardConfig: CardConfig;
  customRenderer?: (
    option: BaseOption,
    isSelected: boolean,
    isDisabled: boolean
  ) => React.ReactNode;
}

const CardRadioGroup = (fieldRenderProps: CardRadioGroupProps) => {
  const {
    validationMessage,
    touched,
    id,
    label,
    valid,
    disabled,
    hint,
    wrapperClassName,
    wrapperStyle,
    astrike = false,
    options,
    value,
    onChange,
    cardConfig,
    customRenderer,
    ...others
  } = fieldRenderProps;

  const editorRef = React.useRef<any>(null);

  const showValidationMessage: string | false | null =
    touched && validationMessage;
  const showHint: boolean = !showValidationMessage && hint;
  const hintId: string = showHint ? `${id}_hint` : "";
  const errorId: string = showValidationMessage ? `${id}_error` : "";
  const labelId: string = label ? `${id}_label` : "";

  const handleCardClick = (optionValue: string) => {
    if (!disabled && onChange) {
      const syntheticEvent = {
        target: { value: optionValue },
        value: optionValue,
      };
      onChange(syntheticEvent);
    }
  };

  // Country card renderer
  const renderCountryCard = (option: BaseOption, isSelected: boolean) => (
    <div className="d-flex align-items-center gap-2">
      <span className="country-code">{option.code}</span>
      <div className="d-flex flex-column">
        <span className="country-name">{option.label}</span>
        <span className="visa-info">
          {option.visaTypes} visa type{option.visaTypes !== 1 ? "s" : ""}{" "}
          available
        </span>
      </div>
      <div className="selection-indicator">
        {isSelected && <div className="selection-dot"></div>}
      </div>
    </div>
  );

  // Visa type card renderer
  const renderVisaCard = (option: BaseOption) => (
    <>
      <div className="card-content">
        <div className="visa-header">
          <h3 className="visa-title">{option.label}</h3>
          {option.visaCode && (
            <span className="visa-code">({option.visaCode})</span>
          )}
        </div>

        {cardConfig.showDescription && option.description && (
          <p className="visa-description">{option.description}</p>
        )}

        {cardConfig.showSubtitle && option.processing && (
          <p className="visa-processing">Processing: {option.processing}</p>
        )}

        {cardConfig.showPrice && option.price && (
          <div className="visa-price">
            {cardConfig.pricePrefix || "₹"}
            {option.price.toLocaleString()}
          </div>
        )}
      </div>

      {/* <div className="selection-indicator">
        {isSelected && <div className="selection-dot"></div>}
      </div> */}
    </>
  );

  // Processing type card renderer
  const renderProcessingCard = (
    option: BaseOption
    // isSelected: boolean,
    // isDisabled: boolean
  ) => (
    <>
      <div className="card-content">
        <div className="processing-header">
          <h3 className="processing-title">{option.label}</h3>
          {cardConfig.showBadge && option.badge && (
            <div
              className={`card-badge ${
                cardConfig.badgePosition || "top-right"
              }`}
            >
              {option.badge}
            </div>
          )}
        </div>

        {cardConfig.showDescription && option.description && (
          <p className="processing-description">{option.description}</p>
        )}

        {cardConfig.showPrice && option.price && (
          <div className="processing-price">
            {cardConfig.pricePrefix || "₹"}
            {option.price.toLocaleString()}
          </div>
        )}
      </div>

      {/* <div className="selection-indicator">
        {isSelected && <div className="selection-dot"></div>}
      </div> */}
    </>
  );

  // Get the appropriate renderer based on layout
  const getCardRenderer = (
    option: BaseOption,
    isSelected: boolean,
    isDisabled: boolean
  ) => {
    if (customRenderer) {
      return customRenderer(option, isSelected, isDisabled);
    }

    switch (cardConfig.layout) {
      case "country":
        return renderCountryCard(option, isSelected);
      case "visa":
        return renderVisaCard(option);
      case "processing":
        return renderProcessingCard(option);
      default:
        return renderCountryCard(option, isSelected);
    }
  };

  // Get container class based on layout
  const getContainerClass = () => {
    const baseClass = "flexible-card-container";
    const layoutClass = `${baseClass}-${cardConfig.layout}`;
    const customClass = cardConfig.containerClass || "";
    return `${baseClass} ${layoutClass} ${customClass}`.trim();
  };

  // Get card class based on layout
  const getCardClass = (isSelected: boolean, isDisabled: boolean) => {
    const baseClass = "flexible-card";
    const layoutClass = `${baseClass}-${cardConfig.layout}`;
    const selectedClass = isSelected ? "flexible-card-selected" : "";
    const disabledClass = isDisabled ? "flexible-card-disabled" : "";
    const customClass = cardConfig.cardClass || "";
    return `${baseClass} ${layoutClass} ${selectedClass} ${disabledClass} ${customClass}`.trim();
  };

  return (
    <FieldWrapper className={wrapperClassName}>
      {label && (
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          className="k-form-label flexible-card-label"
        >
          {label} {astrike && <span style={{ color: "red" }}>*</span>}
        </Label>
      )}

      <div className="k-form-field-wrap" style={wrapperStyle}>
        {/* Hidden RadioGroup for form integration */}
        <RadioGroup
          style={{ display: "none" }}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ariaLabelledBy={labelId}
          valid={valid}
          disabled={disabled}
          ref={editorRef}
          data={options}
          value={value}
          onChange={onChange}
          {...others}
        />

        {/* Flexible Card Design */}
        <div className={getContainerClass()}>
          {options?.map((option) => {
            const isSelected = value === option.value;
            const isDisabled = disabled;

            return (
              <div
                key={option.value}
                className={getCardClass(isSelected, isDisabled)}
                onClick={() => handleCardClick(option.value)}
                role="radio"
                aria-checked={isSelected}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCardClick(option.value);
                  }
                }}
              >
                {getCardRenderer(option, isSelected, isDisabled)}
              </div>
            );
          })}
        </div>

        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error className="d-flex justify-content-end" id={errorId}>
            <span
              className="d-flex align-items-center"
              style={{
                background: "#DF4957",
                color: "white",
                padding: "1px 8px 1px 5px",
                borderRadius: 10,
                gap: 5,
              }}
            >
              <IconComponent icon={CgDanger} /> {validationMessage}
            </span>
          </Error>
        )}
      </div>
    </FieldWrapper>
  );
};

export default CardRadioGroup;
